import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import {
  desktopAndAbove,
  laptopAndAbove,
  largeDesktopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { getFixtureQueryVariables } from 'lib/so5';

import { LazyPrizePoolDrawer } from './LazyPrizePoolDrawer';
import { LivePastLeaderboard } from './LivePastLeaderboard';
import { UpcomingLeaderboard } from './UpcomingLeaderboard';
import type {
  FixtureRewardsQuery,
  FixtureRewardsQueryVariables,
} from './__generated__/index.graphql';

const FIXTURE_REWARDS_QUERY = gql`
  query FixtureRewardsQuery(
    $slug: String
    $type: So5State
    $sport: Sport!
    $isUpcoming: Boolean!
  ) {
    so5 {
      so5Fixture(slug: $slug, type: $type, sport: $sport) {
        slug
        displayName
        so5LeaderboardGroups {
          slug
          displayName
          color
          headerUrl
          so5Leaderboards {
            slug
            so5LeaderboardType
            rarityType
            mySo5LeaderboardContenders {
              slug
            }
            ...UpcomingLeaderboard_so5Leaderboard @include(if: $isUpcoming)
            ...LivePastLeaderboard_so5Leaderboard @skip(if: $isUpcoming)
          }
        }
      }
    }
  }
  ${UpcomingLeaderboard.fragments.so5Leaderboard}
  ${LivePastLeaderboard.fragments.so5Leaderboard}
` as TypedDocumentNode<FixtureRewardsQuery, FixtureRewardsQueryVariables>;

const HeaderContainer = styled(Horizontal).attrs({ center: true })`
  width: 100%;
  position: sticky;
  top: var(--current-stack-height);
  z-index: 1;
  display: flex;
  padding-top: var(--double-unit);

  background-image: linear-gradient(0deg, var(--color) 0%, var(--c-black) 100%);
`;
const HeaderImg = styled.img`
  width: 295px;
`;
const Grid = styled(FullWidthContainer)`
  --nb-items-per-row: 1;
  --gap: var(--double-unit);
  --item-width: calc(
    (100% - (var(--nb-items-per-row) - 1) * var(--gap)) /
      var(--nb-items-per-row)
  );

  background-image: linear-gradient(
    180deg,
    var(--color) 0%,
    rgba(var(--c-rgb-black), 0) 100%
  );
  background-size: 100% min(90%, 600px);
  background-repeat: no-repeat;
  width: 100%;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: var(--gap);

  @media ${laptopAndAbove} {
    --nb-items-per-row: 2;
  }

  @media ${desktopAndAbove} {
    --nb-items-per-row: 3;
  }

  @media ${largeDesktopAndAbove} {
    --nb-items-per-row: 4;
  }

  > * {
    flex-basis: 100%;

    @media ${laptopAndAbove} {
      min-width: var(--item-width);
      flex-basis: var(--item-width);
      flex-grow: 0;
      flex-shrink: 0;
    }
  }
`;

type Props = {
  fixture: string;
  onlyMyCompetitions: boolean;
  isUpcoming: boolean;
  sport: Sport;
};

export const FixtureRewards = ({
  fixture,
  onlyMyCompetitions,
  isUpcoming,
  sport,
}: Props) => {
  const openDialog = useOpenDialog();
  const [drawerSo5LeaderboardSlug, setDrawerSo5LeaderboardSlug] =
    useState<string>();

  const { data, loading } = useQuery(FIXTURE_REWARDS_QUERY, {
    variables: {
      ...getFixtureQueryVariables(fixture),
      sport,
      isUpcoming,
    },
  });

  const { so5Fixture } = data?.so5 || {};
  const { so5LeaderboardGroups = [] } = so5Fixture || {};

  if (loading) {
    return <LoadingIndicator />;
  }

  if (!so5Fixture) {
    return null;
  }

  return (
    <Vertical gap={3}>
      {so5LeaderboardGroups.map(so5LeaderboardGroupInterface => {
        const sortedLeaderboards = [
          ...(so5LeaderboardGroupInterface.so5Leaderboards || []),
        ].sort(
          (a, b) =>
            a.rarityType.localeCompare(b.rarityType) ||
            a.so5LeaderboardType.localeCompare(b.so5LeaderboardType)
        );

        const displayedLeaderboards = onlyMyCompetitions
          ? sortedLeaderboards.filter(
              so5Leaderboard =>
                onlyMyCompetitions &&
                so5Leaderboard.mySo5LeaderboardContenders.length > 0
            )
          : sortedLeaderboards;

        if (!displayedLeaderboards.length) {
          return null;
        }

        return (
          <ContentWithStickyHeader
            key={so5LeaderboardGroupInterface.slug}
            header={
              <HeaderContainer
                style={
                  {
                    '--color': so5LeaderboardGroupInterface.color,
                  } as CSSProperties
                }
              >
                <HeaderImg
                  src={so5LeaderboardGroupInterface.headerUrl}
                  alt={so5LeaderboardGroupInterface.displayName}
                />
              </HeaderContainer>
            }
          >
            <Grid
              style={
                {
                  '--color': so5LeaderboardGroupInterface.color,
                } as CSSProperties
              }
            >
              {displayedLeaderboards.map(so5Leaderboard =>
                isUpcoming ? (
                  <UpcomingLeaderboard
                    key={so5Leaderboard.slug}
                    so5Leaderboard={so5Leaderboard}
                    onClick={() =>
                      setDrawerSo5LeaderboardSlug(so5Leaderboard.slug)
                    }
                  />
                ) : (
                  <LivePastLeaderboard
                    key={so5Leaderboard.slug}
                    so5Leaderboard={so5Leaderboard}
                    onClick={() =>
                      setDrawerSo5LeaderboardSlug(so5Leaderboard.slug)
                    }
                    onClickRanking={value =>
                      openDialog(DialogKey.contender, value)
                    }
                  />
                )
              )}
            </Grid>
          </ContentWithStickyHeader>
        );
      })}
      <LazyPrizePoolDrawer
        open={!!drawerSo5LeaderboardSlug}
        onClose={() => setDrawerSo5LeaderboardSlug(undefined)}
        so5LeaderboardSlug={drawerSo5LeaderboardSlug}
      />
    </Vertical>
  );
};
